import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { buildSystemPromptFromTemplate, CHATBOT_PROMPT_TEMPLATES } from '@workspace/types';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { TOOL_OPTIONS, toolsToPersist } from '../api/agentTools';
import { contactAgentApi } from '../api/contactAgentApi';
import { contactAgentError } from '../api/contactAgentScope';
import { ContactAgentPage, ContactAgentPageHeader } from '../components/ContactAgentPageHeader';
import { ContactAgentTenantPicker } from '../components/ContactAgentTenantPicker';
import { ContactAgentSourceFields } from '../components/ContactAgentSourceFields';
import { EscalationRosterFields } from '../components/EscalationRosterFields';
import { useContactAgentTenant } from '../hooks/useContactAgentTenant';
import type { ContactAgentAvailableBot, PortfolioConnectionPublic } from '@workspace/types';

type BotOption = ContactAgentAvailableBot;

const IDENTIFICATION_METHOD_LABELS: Record<string, string> = {
  WHATSAPP_CONTACTO_PORTFOLIO: 'WhatsApp contacto + cartera',
  COBRANZAS_PORTFOLIO: 'Cartera cargada (web / campañas)',
  WHATSAPP_WHITELIST: 'WhatsApp lista blanca',
  WEB_VERIFIED_OTP: 'Web con OTP',
  WEB_WHITELIST: 'Web lista blanca',
  WEB_DOMAIN: 'Web por dominio',
};

function formatIdentificationMethods(methods: string[]): string {
  return methods.map(method => IDENTIFICATION_METHOD_LABELS[method] ?? method).join(' · ');
}

export function ContactAgentNewPage() {
  const navigate = useNavigate();
  const { agentId } = useParams<{ agentId: string }>();
  const isNew = !agentId || agentId === 'new';
  const { tenantId, tenants, isSuperAdmin, ready, selectTenant } = useContactAgentTenant();
  const [bots, setBots] = useState<BotOption[]>([]);
  const [botId, setBotId] = useState('');
  const [botName, setBotName] = useState('');
  const [name, setName] = useState('Cobranzas');
  const [objective, setObjective] = useState(
    'Ayudar al cliente a regularizar su deuda con datos reales, sin presion indebida.',
  );
  const [systemPrompt, setSystemPrompt] = useState('');
  const [templateId, setTemplateId] = useState('debt-collection');
  const [tools, setTools] = useState<string[]>(TOOL_OPTIONS.map(item => item.name));
  const [paymentMethodsText, setPaymentMethodsText] = useState(
    '- Transferencia: CBU ...\n- Mercado Pago: alias ...',
  );
  const [sourceConnectionId, setSourceConnectionId] = useState<string | null>(null);
  const [sourceConnection, setSourceConnection] = useState<PortfolioConnectionPublic | null>(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!ready || !isNew) {
      return;
    }
    setSourceConnectionId(null);
    setSourceConnection(null);
  }, [tenantId, ready, isNew]);

  useEffect(() => {
    if (!ready) {
      return;
    }
    if (!isNew && agentId) {
      void contactAgentApi
        .getAgent(agentId)
        .then(agent => {
          setBotId(agent.botId);
          setBotName(agent.bot?.name ?? agent.name);
          setName(agent.name);
          setObjective(agent.objective ?? '');
          setTools(agent.toolsEnabled.length > 0 ? agent.toolsEnabled : TOOL_OPTIONS.map(item => item.name));
          setPaymentMethodsText(agent.endRules?.paymentMethodsText ?? '');
          setSystemPrompt(agent.bot?.systemPrompt ?? '');
          setSourceConnectionId(agent.sourceConnectionId);
          setSourceConnection(agent.sourceConnection ?? null);
        })
        .catch(err => setError(contactAgentError(err)));
      return;
    }

    setBotId('');
    setBotName('');
    setSystemPrompt(buildSystemPromptFromTemplate('debt-collection'));
    setTemplateId('debt-collection');
    setError('');
    void contactAgentApi
      .listAvailableBots()
      .then(options => {
        setBots(options);
        const firstEligible = options.find(bot => bot.identificationEligible);
        if (firstEligible) {
          setBotId(firstEligible.botId);
        } else if (options[0]) {
          setBotId(options[0].botId);
        }
      })
      .catch(err => setError(contactAgentError(err)));
  }, [ready, tenantId, isNew, agentId]);

  const applyPromptTemplate = (nextTemplateId: string) => {
    setTemplateId(nextTemplateId);
    setSystemPrompt(buildSystemPromptFromTemplate(nextTemplateId));
  };

  const toggleTool = (toolName: string) => {
    setTools(current =>
      current.includes(toolName) ? current.filter(item => item !== toolName) : [...current, toolName],
    );
  };

  const selectedBot = bots.find(bot => bot.botId === botId);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!botId || !name.trim()) {
      setError('Elegi un bot y un nombre.');
      return;
    }
    if (isNew && selectedBot && !selectedBot.identificationEligible) {
      setError(
        selectedBot.identificationIssues[0] ??
          'El bot debe tener al menos un método de identificación unívoca (whitelist o verificación web).',
      );
      return;
    }
    if (tools.includes('obtener_medios_pago') && !paymentMethodsText.trim()) {
      setError('Configura los medios de pago o desactiva esa herramienta.');
      return;
    }
    if (!systemPrompt.trim()) {
      setError('El prompt del bot no puede quedar vacio.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: name.trim().slice(0, 255),
        objective: objective.trim().slice(0, 4000),
        toolsEnabled: toolsToPersist(tools),
        paymentMethodsText: paymentMethodsText.trim().slice(0, 2000),
        systemPrompt: systemPrompt.trim().slice(0, 32000),
        sourceConnectionId,
      };
      if (isNew) {
        await contactAgentApi.createAgent({
          botId,
          domain: 'COBRANZAS',
          ...payload,
        });
      } else if (agentId) {
        await contactAgentApi.updateAgent(agentId, payload);
      }
      navigate('/admin/contact-agent');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo guardar el agente');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ContactAgentPage>
      <ContactAgentPageHeader
        title={isNew ? 'Nuevo agente' : 'Editar agente'}
        description='Cobranzas: WhatsApp de contacto con cartera importada, whitelist en canal, o acceso web verificado. Sin contacto identificado el bot solo pide teléfono/factura (web) o usa el número de WhatsApp. Acceso directo o vía conserje según el bot.'
        toolbar={
          isSuperAdmin ? (
            <ContactAgentTenantPicker tenants={tenants} tenantId={tenantId} onChange={selectTenant} />
          ) : null
        }
      />

      <Card className='rounded-2xl shadow-sm'>
        <CardHeader>
          <CardTitle>Datos del agente</CardTitle>
        </CardHeader>
        <CardContent>
          <form className='space-y-6' onSubmit={handleSubmit}>
            {ready ? (
              <ContactAgentSourceFields
                key={tenantId}
                tenantId={tenantId}
                sourceConnectionId={sourceConnectionId}
                sourceConnection={sourceConnection}
                onBound={(connectionId, connection) => {
                  setSourceConnectionId(connectionId);
                  setSourceConnection(connection ?? null);
                }}
              />
            ) : null}
            <div className='space-y-2'>
              <Label htmlFor='botId'>Bot</Label>
              {isNew ? (
                <select
                  id='botId'
                  className='flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm'
                  value={botId}
                  onChange={event => setBotId(event.target.value)}
                >
                  {bots.length === 0 ? <option value=''>No hay bots disponibles</option> : null}
                  {bots.map(bot => (
                    <option key={bot.botId} value={bot.botId} disabled={!bot.identificationEligible}>
                      {bot.name}
                      {!bot.identificationEligible ? ' (sin identificación)' : ''}
                    </option>
                  ))}
                </select>
              ) : (
                <Input id='botId' value={botName || botId} disabled />
              )}
              {isNew && selectedBot && !selectedBot.identificationEligible ? (
                <div className='rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm'>
                  <p className='font-medium text-destructive'>Este bot no puede usarse como agente de cobranzas</p>
                  <p className='mt-1 text-muted-foreground'>
                    {selectedBot.identificationIssues.join(' ') ||
                      'Configurá whitelist en WhatsApp/Telegram o acceso web verificado en la instancia del bot.'}
                  </p>
                </div>
              ) : null}
              {isNew && selectedBot?.identificationEligible ? (
                <p className='text-xs text-muted-foreground'>
                  Listo para cobranzas: {formatIdentificationMethods(selectedBot.identificationMethods)}.
                </p>
              ) : null}
              {botId ? (
                <p className='text-xs text-muted-foreground'>
                  Tambien podés editar modelo y API key en{' '}
                  <Link className='underline' to={`/admin/chatbot/bots/${botId}`}>
                    Chatbot → Bots
                  </Link>
                  .
                </p>
              ) : null}
            </div>
            <div className='space-y-2'>
              <Label htmlFor='name'>Nombre</Label>
              <Input id='name' value={name} maxLength={255} onChange={event => setName(event.target.value)} />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='promptTemplate'>Plantilla de prompt</Label>
              <div className='flex flex-wrap items-end gap-2'>
                <select
                  id='promptTemplate'
                  className='flex h-10 min-w-[16rem] flex-1 rounded-md border border-input bg-background px-3 text-sm'
                  value={templateId}
                  onChange={event => applyPromptTemplate(event.target.value)}
                >
                  {CHATBOT_PROMPT_TEMPLATES.map(template => (
                    <option key={template.id} value={template.id}>
                      {template.name} — {template.description}
                    </option>
                  ))}
                </select>
                <Button type='button' variant='outline' onClick={() => applyPromptTemplate(templateId)}>
                  Aplicar plantilla
                </Button>
              </div>
              <p className='text-xs text-muted-foreground'>
                Para cobranzas usá <strong>Cobrador / cobranzas</strong>. El prompt se guarda en el bot al crear el
                agente.
              </p>
            </div>
            <div className='space-y-2'>
              <Label htmlFor='systemPrompt'>Prompt del bot</Label>
              <Textarea
                id='systemPrompt'
                rows={10}
                maxLength={32000}
                value={systemPrompt}
                onChange={event => setSystemPrompt(event.target.value)}
              />
              <p className='text-xs text-muted-foreground'>
                Este es el system prompt del bot (el que se migró desde Flowise). Lo usa la IA en
                cada respuesta. Abajo se agregan las reglas de cobranzas y los datos de las
                herramientas.
              </p>
            </div>
            <div className='space-y-2'>
              <Label htmlFor='objective'>Objetivo de cobranzas</Label>
              <Textarea
                id='objective'
                rows={3}
                maxLength={4000}
                value={objective}
                onChange={event => setObjective(event.target.value)}
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='paymentMethodsText'>Medios de pago</Label>
              <Textarea
                id='paymentMethodsText'
                rows={4}
                maxLength={2000}
                value={paymentMethodsText}
                onChange={event => setPaymentMethodsText(event.target.value)}
                placeholder='CBU, alias, links o instrucciones que el agente enviará al cliente.'
              />
            </div>
            <div className='space-y-3'>
              <div>
                <Label>Herramientas</Label>
                <p className='mt-1 text-sm text-muted-foreground'>
                  Reclamos, comprobantes y pagos informados van a Excepciones para un operador.
                </p>
              </div>
              <div className='grid gap-2 md:grid-cols-2'>
                {TOOL_OPTIONS.map(tool => {
                  const active = tools.includes(tool.name);
                  return (
                    <button
                      key={tool.name}
                      type='button'
                      onClick={() => toggleTool(tool.name)}
                      className={`rounded-xl border p-3 text-left transition-colors ${
                        active ? 'border-primary bg-primary/5' : 'hover:bg-muted/40'
                      }`}
                    >
                      <span
                        className={`block text-sm font-medium ${
                          active ? 'text-primary' : 'text-foreground'
                        }`}
                      >
                        {tool.label}
                      </span>
                      <span className='mt-1 block text-xs text-muted-foreground'>
                        {tool.description}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
            {error ? <p className='text-sm text-destructive'>{error}</p> : null}
            <div className='flex justify-end gap-2'>
              <Button type='button' variant='outline' onClick={() => navigate('/admin/contact-agent')}>
                Cancelar
              </Button>
              <Button
                type='submit'
                disabled={saving || !botId || (isNew && selectedBot ? !selectedBot.identificationEligible : false)}
              >
                {saving ? 'Guardando...' : isNew ? 'Crear agente' : 'Guardar'}
              </Button>
            </div>
          </form>
          {!isNew && agentId ? (
            <div className='mt-8'>
              <EscalationRosterFields agentId={agentId} apiBase='/admin/contact-agent' />
            </div>
          ) : null}
        </CardContent>
      </Card>
    </ContactAgentPage>
  );
}
